import {useState, useEffect, Suspense, useContext, lazy} from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useHistory,
} from 'react-router-dom';
import {CommandPaletteService} from 'erp-react-components';
import {ScrollToTop} from './components/ScrollToTop';
import {DataContextProvider} from './contexts/data.context';
import {AuthContextProvider, AuthContext} from './contexts/auth.context';
import {Loading} from 'components/LoadingComponents/loading';
import Login from './components/login';
import ForgotPassword from './components/forgot-password';
import LoginSignup from './components/login-signup';
import ResetPassword from './components/reset-password';
import {RestfulProvider} from 'restful-react';
import {Toast} from 'erp-react-components';
import {library} from '@fortawesome/fontawesome-svg-core';

import {
  faAngleDown,
  faAngleRight,
  faChevronCircleLeft,
  faChevronCircleRight,
  faAngleUp,
  faPlus,
  faStar,
  faStarHalf,
  faTimes,
  faBold,
  faUnderline,
  faItalic,
  faListOl,
  faListUl,
  faArrowsAlt,
  faThumbtack,
  faReply,
  faUpload,
  faAngleLeft,
  faLongArrowAltDown,
  faLongArrowAltUp,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import {ErrorBoundary} from 'react-error-boundary';
import Walkthrough from 'components/Walkthrough';
import {QueryClient, QueryClientProvider} from 'react-query';
import {ReactQueryDevtools} from 'react-query/devtools';
import LoginError from 'components/login-error';
import ERPCommandPalette from 'ERPCommandPalette';
import {languageSelectOptions} from 'helpers/constants';
import {safelyParseJson} from 'helpers/helpers';
import WithErrorBoundary from 'components/WithErrorBoundary';
import {useTranslation} from 'react-i18next';
import InternalJobBoardList from './job-board-list/internal-jobboard-list';
import ViewJob from 'viewJob';
import ApplyJob from './applyjob/index';
import NavBar from 'components/Navbar/topNavBar';
import AppliedList from 'AppliedList';
import ViewApplication from 'viewApplication';
import Profile from 'Profile';

library.add(
  faAngleRight,
  faAngleLeft,
  faChevronCircleLeft,
  faChevronCircleRight,
  faTimes,
  faAngleUp,
  faAngleDown,
  faStar,
  faStarHalf,
  faPlus,
  faBold,
  faUnderline,
  faItalic,
  faListOl,
  faListUl,
  faArrowsAlt,
  faThumbtack,
  faReply,
  faUpload,
  // faArrowUpArrowDown,
  faLongArrowAltDown,
  faLongArrowAltUp,
  faSortUp,
  faSortDown,
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30000,
      cacheTime: 5 * 60000,
    },
  },
});
const INTERNALJOB = lazy(() =>
  import('./job-board-list/internal-jobboard-list'),
);
const VIEW_JOB = lazy(() => import('./viewJob/index'));
const APPLY_JOB = lazy(() => import('./applyjob/index'));
const VIEW_APPLICATION = lazy(() => import('./viewApplication/index'));
const PROFILE = lazy(() => import('./Profile/index'));
function App() {
  return (
    <RestfulProvider>
      <Suspense
        fallback={<div className="awl-container">{/* <Loading /> */}</div>}
      >
        <AuthContextProvider>
          <QueryClientProvider client={queryClient}>
            <Home />
            <ReactQueryDevtools />
          </QueryClientProvider>
        </AuthContextProvider>
        <Toast />
      </Suspense>
    </RestfulProvider>
  );
}

const Home = (props) => {
  const authContext = useContext(AuthContext);
  useEffect(() => {
    if (authContext.isLoggedIn !== null) {
      const langDetails = localStorage.getItem('choosen_language')
        ? safelyParseJson(
            localStorage.getItem('choosen_language'),
            languageSelectOptions[0],
          )
        : languageSelectOptions[0];
      authContext.changeLanguage(langDetails);
    }
  }, [authContext.isLoggedIn]);

  if (authContext.loginError) {
    document.getElementById('initial-loading-overlay') &&
      document.body.removeChild(
        document.getElementById('initial-loading-overlay'),
      );
    if (document.getElementById('side-menu-wrapper')) {
      document.getElementById('side-menu-wrapper').style.display = 'none';
    }
    return (
      <div className="awl-container">
        <LoginError
          message={authContext.loginError}
          onTryAgain={authContext.handleLogout}
        />
      </div>
    );
  }
  if (authContext.isLoggedIn) {
    //remove loader overlay
    document.getElementById('initial-loading-overlay') &&
      document.body.removeChild(
        document.getElementById('initial-loading-overlay'),
      );
  } else {
    return <></>;
  }
  return <Main />;
};
const MainRoutes = ({routes}) => {
  const history = useHistory();
  const onSelect = (url) => {
    history.push(url);
  };
  const filteredList = routes.filter((eachRoute) => {
    const isColonExists = (eachRoute.path || '').includes(':');
    return !isColonExists;
  });
  const urlList = filteredList.map((eachRoute) => {
    return {
      url: eachRoute.path,
      label: eachRoute.label,
      keywords: eachRoute.keywords,
      onClick: () => onSelect(eachRoute.path),
    };
  });

  useEffect(() => {
    CommandPaletteService.addRoutes(urlList);
  }, []);
  return (
    <Switch>
      <Route path="/" exact>
        <Redirect to={'/internal/jobboard'} />
      </Route>
      {routes.map((r) => {
        return (
          <Route
            key={r.path}
            path={r.path}
            component={WithErrorBoundary(r.component)}
            exact={r.exact}
          />
        );
      })}
      <Route
        path="/internal/jobboard"
        component={INTERNALJOB}
        render={(props) => {
          return <INTERNALJOB {...props} />;
        }}
      />
      <Route
        path="/internal/viewJob/:postId"
        component={VIEW_JOB}
        render={(props) => {
          return <VIEW_JOB {...props} />;
        }}
      />
      <Route
        path="/internal/applyjob/:postId"
        component={APPLY_JOB}
        render={(props) => {
          return <APPLY_JOB {...props} />;
        }}
      />
      <Route
        path="/view/application/:postId"
        component={VIEW_APPLICATION}
        render={(props) => {
          return <VIEW_APPLICATION {...props} />;
        }}
      />
      <Route
        path="/jobboard/profile"
        component={PROFILE}
        render={(props) => {
          return <PROFILE {...props} />;
        }}
      />
      <Route path="*">
        <Redirect to={'/internal/jobboard'} />
      </Route>
    </Switch>
  );
};

const Main = () => {
  const [walkthroughIndex, setWalkThroughIdx] = useState('');
  const {t} = useTranslation();
  const getIndexClick = (index) => {
    setWalkThroughIdx(index || '');
  };

  const JOB_BOARD_ROUTES = [
    {
      path: '/jobboard',
      component: InternalJobBoardList,
      exact: true,
      label: 'Internal Job Board',
      keywords: 'Internal,Job Board,Internal Job Board',
    },
    {
      path: '/viewJob/:postId',
      component: ViewJob,
      exact: true,
      label: 'View Job',
      keywords: 'view,Job,View Job',
    },
    {
      path: '/applyjob/:postId',
      component: ApplyJob,
      exact: true,
      label: 'Apply Job',
      keywords: 'apply,Job,Apply Job',
    },
    {
      path: '/jobboard/applied',
      component: AppliedList,
      exact: true,
      label: 'Applied List',
      keywords: 'Applied,Job,Applied Job,Applied Job List',
    },
    {
      path: '/view/application',
      component: ViewApplication,
      exact: true,
      label: 'View Application',
      keywords: 'View Application,View,Application',
    },
  ];
  return (
    <DataContextProvider>
      <DebugRouter debug={true}>
        <ScrollToTop />

        {/* scroll to top after route change */}

        <div id="main-app-content" className="App d-flex flex-column">
          <Walkthrough getIndexClick={getIndexClick} />
          <ERPCommandPalette />
          <NavBar />
          <ErrorBoundary
            FallbackComponent={({error, resetErrorBoundary}) => (
              <div className="awl-container justify-content-center align-items-center">
                <h4>
                  {t('An Error occured while performing the last action')}
                </h4>
                <br />
                <button
                  onClick={() => window.location.reload()}
                  className="btn btn-secondary"
                >
                  Try again
                </button>
              </div>
            )}
            onError={(error, info) => {
              //todo: setstate and use the state in sidemenu to refresh the page to that url, instead of navigating with react-router
              //call api
              /*eslint-disable-next-line no-console*/
              console.log('error boundary', error, info);
            }}
            onReset={() => {
              /*eslint-disable-next-line no-console*/
              console.log('reset triggered');
            }}
          >
            <Suspense
              fallback={
                <div className="loading-container">
                  <Loading />
                </div>
              }
            >
              <MainRoutes routes={JOB_BOARD_ROUTES} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </DebugRouter>
    </DataContextProvider>
  );
};

export function UnAuthenticatedApp(props) {
  return (
    <Router>
      <Switch>
        <Route path="/" exact={true} component={Login} />
        <Route
          path="/reset-password/:userId"
          exact={true}
          component={ResetPassword}
        />
        <Route exact path="/forgot-password" component={ForgotPassword} />
        <Route exact path="/login-signup" component={LoginSignup} />
      </Switch>
    </Router>
  );
}

export default App;

class DebugRouter extends Router {
  constructor(props) {
    super(props);
    if (props.debug) {
      /*eslint-disable-next-line no-console*/
      // console.log(
      //   'initial history is: ',
      //   JSON.stringify(this.history, null, 2),
      // );
      this.history.listen((location, action) => {
        /*eslint-disable-next-line no-console*/
        console.log(
          `The current URL is ${location.pathname}${location.search}${location.hash}`,
        );
        /*eslint-disable-next-line no-console*/
        // console.log(
        //   `The last navigation action was ${action}`,
        //   JSON.stringify(this.history, null, 2),
        // );
      });
    }
  }
}
